package org.gymly.ui.admin

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.outlined.AccountBalance
import androidx.compose.material.icons.outlined.AttachMoney
import androidx.compose.material.icons.outlined.CreditCard
import androidx.compose.material.icons.outlined.CurrencyBitcoin
import androidx.compose.material.icons.outlined.Payments
import androidx.compose.material.icons.outlined.PhoneAndroid
import androidx.compose.material.icons.outlined.Send
import androidx.compose.material3.Icon
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import org.gymly.model.AdminPaymentMethod
import org.gymly.theme.AdminColors

fun adminPaymentMethodIcon(method: AdminPaymentMethod): ImageVector = when (method) {
    AdminPaymentMethod.CASH -> Icons.Outlined.Payments
    AdminPaymentMethod.CARD -> Icons.Outlined.CreditCard
    AdminPaymentMethod.TRANSFER -> Icons.Outlined.AccountBalance
    AdminPaymentMethod.MOBILE_PAYMENT -> Icons.Outlined.PhoneAndroid
    AdminPaymentMethod.ZELLE -> Icons.Outlined.Send
    AdminPaymentMethod.BIZUM -> Icons.Outlined.Send
    AdminPaymentMethod.USDT -> Icons.Outlined.CurrencyBitcoin
    AdminPaymentMethod.DIVISAS -> Icons.Outlined.AttachMoney
    AdminPaymentMethod.ONLINE -> Icons.Outlined.CreditCard
}

@Composable
fun AdminPaymentMethodIconGrid(
    selected: AdminPaymentMethod,
    onSelect: (AdminPaymentMethod) -> Unit,
    enabled: Boolean = true,
    columnsPerRow: Int = 4,
    paymentTypes: List<org.gymly.model.AdminPaymentMethodType>? = null,
) {
    val methods = if (paymentTypes != null) {
        paymentTypes
            .filter { it.enabled }
            .mapNotNull { t -> AdminPaymentMethod.entries.firstOrNull { it.name.equals(t.code, ignoreCase = true) } }
            .ifEmpty { AdminPaymentMethod.adminChoices() }
    } else {
        AdminPaymentMethod.adminChoices()
    }
    val columns = columnsPerRow.coerceIn(2, 4)
    Column(
        modifier = Modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(8.dp),
    ) {
        methods.chunked(columns).forEach { rowMethods ->
            Row(
                Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(8.dp),
            ) {
                rowMethods.forEach { method ->
                    AdminPaymentMethodIconChip(
                        method = method,
                        selected = method == selected,
                        enabled = enabled,
                        onClick = { onSelect(method) },
                        modifier = Modifier.weight(1f),
                        wide = true,
                    )
                }
                repeat(columns - rowMethods.size) {
                    Spacer(Modifier.weight(1f))
                }
            }
        }
    }
}

@Composable
fun AdminPaymentMethodIconChip(
    method: AdminPaymentMethod,
    selected: Boolean,
    enabled: Boolean = true,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    wide: Boolean = false,
) {
    val borderColor = if (selected) AdminColors.Primary else AdminColors.Outline
    val bg = if (selected) AdminColors.Primary.copy(alpha = 0.1f) else AdminColors.Surface
    val chipModifier = modifier
        .clip(RoundedCornerShape(12.dp))
        .border(1.dp, borderColor, RoundedCornerShape(12.dp))
        .background(bg)
        .clickable(enabled = enabled, onClick = onClick)

    if (wide) {
        Row(
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.Center,
            modifier = chipModifier
                .fillMaxWidth()
                .padding(horizontal = 10.dp, vertical = 12.dp),
        ) {
            Icon(
                adminPaymentMethodIcon(method),
                contentDescription = method.labelEs(),
                tint = if (selected) AdminColors.Primary else AdminColors.TextMuted,
                modifier = Modifier.size(22.dp),
            )
            Spacer(Modifier.size(8.dp))
            Text(
                method.labelEs(),
                fontSize = 12.sp,
                fontWeight = if (selected) FontWeight.SemiBold else FontWeight.Medium,
                color = if (selected) AdminColors.Primary else AdminColors.Text,
                maxLines = 1,
            )
        }
    } else {
        Column(
            horizontalAlignment = Alignment.CenterHorizontally,
            modifier = chipModifier.padding(horizontal = 6.dp, vertical = 8.dp),
        ) {
            Icon(
                adminPaymentMethodIcon(method),
                contentDescription = method.labelEs(),
                tint = if (selected) AdminColors.Primary else AdminColors.TextMuted,
                modifier = Modifier.size(22.dp),
            )
            Spacer(Modifier.height(4.dp))
            Text(
                method.labelEs(),
                fontSize = 10.sp,
                fontWeight = if (selected) FontWeight.SemiBold else FontWeight.Normal,
                color = if (selected) AdminColors.Primary else AdminColors.TextMuted,
                maxLines = 1,
            )
        }
    }
}

fun combinedPaymentStatusLabel(methods: List<AdminPaymentMethod>): String =
    if (methods.isEmpty()) {
        "Pagado"
    } else if (methods.any { it.requiresManualApproval() }) {
        "Pendiente · Combinado"
    } else {
        "Pagado · Combinado"
    }
